import type { QueryObserverResult } from 'react-query';
import { parseErrors } from 'helpers/parseResponseErrors';
import { Toast } from 'helpers/toast';
import { getMessages } from 'queries/common/getMessages';
import { useQuery } from 'react-query';
import { JsonDataItem, Message } from 'types';

const queryGroup = 'MESSAGES_LIST';

type PaginationParams = {
  page: number;
  recordsPerPage: number;
};

type Response<T extends (...args: any) => any> = Awaited<ReturnType<T>>;
type MessagesResponse = Response<typeof getMessages>;
type MessageResponse = Response<typeof getMessages>['data'] extends Message[]
  ? Response<typeof getMessages>['data']
  : never;
type AllowedMessageTypes = Parameters<typeof getMessages>['1'];
type AllowedResourceTypes = Parameters<typeof getMessages>['0'];
type RefetchFn = () => Promise<QueryObserverResult<MessagesResponse, JsonDataItem>>;

export function useMessages<R extends AllowedResourceTypes, M extends AllowedMessageTypes>(
  resourceType: R,
  messageType: M,
  resourceId: string,
  page: number,
  recordsPerPage: number,
): [messages: MessageResponse | undefined, totalMessages: number | undefined, isLoading: boolean, refetch: RefetchFn] {
  const { data, isLoading, refetch } = useQuery({
    queryFn: () => getMessages(resourceType, messageType, resourceId, { page, recordsPerPage }),
    queryKey: [queryGroup, resourceType, resourceId, messageType, { page, recordsPerPage } as PaginationParams],
    onError(err: JsonDataItem) {
      Toast.show({ description: parseErrors(err).join(', '), type: Toast.Type.Error });
    },
  });

  return [data?.data, data?.meta.total, isLoading, refetch];
}
